/**
 * «Facturar a otro nombre» (spec 08, enmienda 2026-10-29). Desde una factura (o una nota de entrega) ya pagada: nota de
 * crédito por el total (o Cambio, en la nota) sin mover inventario y factura (o nota) nueva al cliente que se elige, con
 * la misma información de la original y pagada con el crédito de esa nota de crédito. Sin el permiso de la dueña
 * (`ventas.otro-nombre`) se pide la autorización «Facturar a otro nombre», con clave o a distancia. Lo impreso sale en la
 * ventana fija, en orden: primero la nota de crédito y después la factura nueva.
 */
import type { ClienteDTO, DocumentoDTO } from '@mg/shared';
import { Search, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { useAutorizacion } from '@/componentes/Autorizacion.tsx';
import { Dinero } from '@/componentes/Dinero.tsx';
import { mostrarRegistrado } from '@/componentes/Registrado.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Textarea } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { mensajeDeError } from '@/lib/api.ts';
import { useClientes } from '@/modulos/clientes/api.ts';
import { DialogClienteRapido } from '@/modulos/clientes/Clientes.tsx';
import { useOtroNombre } from '@/modulos/devoluciones/api.ts';

export function DialogoOtroNombre({ doc, abierto, cerrar }: { doc: DocumentoDTO; abierto: boolean; cerrar: () => void }) {
  const navigate = useNavigate();
  const esFac = doc.tipo === 'FAC';
  const [q, setQ] = useState('');
  const [cliente, setCliente] = useState<ClienteDTO | null>(null);
  const [motivo, setMotivo] = useState('');
  const [nuevo, setNuevo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  // Clave de operación: la misma mientras se reintenta (doble clic, respuesta perdida); otra al terminar
  const [clave, setClave] = useState(() => crypto.randomUUID());
  const buscar = q.trim().length >= 2 && !cliente;
  const lista = useClientes({ q: q.trim(), limite: 8 }, buscar);
  const otroNombre = useOtroNombre();
  const aut = useAutorizacion();
  const candidatos = (lista.data?.datos ?? []).filter((c) => c.id !== doc.cliente_id && !c.consumidor_final && c.activo);
  const sinDatos = !!cliente && esFac && !cliente.apto_factura;
  const retiene = !!cliente && esFac && (cliente.tipo_contribuyente === 'especial' || cliente.municipal);
  const t = { tasa_bcv: doc.tasa_bcv, tasa_paralela: doc.tasa_aplicada };
  const nombreDoc = esFac ? 'la factura' : 'la nota';
  const nombreCredito = esFac ? 'nota de crédito' : 'Cambio';

  const limpiar = () => {
    setQ('');
    setCliente(null);
    setMotivo('');
    setClave(crypto.randomUUID());
    aut.limpiar();
  };

  const confirmar = async () => {
    if (!cliente) return;
    setEnviando(true);
    try {
      const r = await aut.ejecutar((ids) => otroNombre.mutateAsync({ datos: { documento_origen_id: doc.id, cliente_id: cliente.id, motivo: motivo.trim(), autorizaciones: ids }, clave }), {
        motivo: motivo.trim(),
        detalle: { documento: `${doc.tipo} ${doc.numero_formateado}`, de: doc.cliente_razon_social, a: `${cliente.razon_social} (${cliente.rif})` },
        montoUsd: doc.total,
      });
      if (!r) return;
      const nueva = r.despacho;
      mostrarRegistrado({
        titulo: `${doc.numero_formateado} pasada a nombre de ${cliente.razon_social}`,
        descripcion: `${esFac ? 'Nota de crédito' : 'Cambio'} ${r.numero_formateado} y ${esFac ? 'factura' : 'nota'} nueva ${nueva?.numero_formateado ?? ''}, pagada con ese crédito.`,
        detalle: [
          ...(esFac ? [{ texto: `Imprime en este orden, cada una en su hoja preimpresa: primero la nota de crédito${r.control_formateado ? ` (control ${r.control_formateado})` : ''} y después la factura nueva${nueva?.control_formateado ? ` (control ${nueva.control_formateado})` : ''}.`, aviso: true }] : []),
          { texto: `${esFac ? 'Nota de crédito' : 'Cambio'} ${r.numero_formateado} a ${doc.cliente_razon_social}`, monto: { valor: r.total, bs: r.total_bs, doc: t, testid: 'registrado-otro-nombre-nc' } },
          ...(nueva ? [{ texto: `${esFac ? 'Factura' : 'Nota'} nueva ${nueva.numero_formateado} a ${cliente.razon_social}`, monto: { valor: nueva.total, bs: nueva.total_bs, doc: { tasa_bcv: nueva.tasa_bcv, tasa_paralela: nueva.tasa_aplicada }, testid: 'registrado-otro-nombre-nueva' } }] : []),
          { texto: 'No se cobró otra vez ni se repitió la referencia del pago: la nueva queda pagada con el crédito. Sin IGTF.' },
        ],
        imprimir: [
          { etiqueta: `1 · Imprimir ${esFac ? 'nota de crédito' : 'cambio'}${r.control_formateado ? ` (control ${r.control_formateado})` : ''}`, ruta: `/imprimir/${r.tipo}/${r.id}` },
          ...(nueva ? [{ etiqueta: `2 · Imprimir ${esFac ? 'factura nueva' : 'nota nueva'}${nueva.control_formateado ? ` (control ${nueva.control_formateado})` : ''}`, ruta: `/imprimir/${nueva.tipo}/${nueva.id}` }] : []),
        ],
        ...(nueva ? { alCerrar: () => navigate(`/documentos/${nueva.id}`) } : {}),
      });
      limpiar();
      cerrar();
    } catch (e) {
      toast.error('No se pudo pasar a otro nombre', { description: mensajeDeError(e) });
      setClave(crypto.randomUUID());
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <Dialog open={abierto} onOpenChange={(o) => { if (!o) cerrar(); }}>
        <DialogContent className="max-w-xl" data-testid="dialogo-otro-nombre">
          <DialogHeader>
            <div className="flex items-center gap-1"><DialogTitle>{esFac ? 'Facturar a otro nombre' : 'Pasar la nota a otro nombre'}</DialogTitle><AyudaVentana tema="documentos.otro-nombre" /></div>
            <DialogDescription>
              {esFac
                ? `Para cuando el cliente pide la factura a nombre de otra persona o de su empresa. ${doc.numero_formateado} se revierte con una nota de crédito por el total y sale una factura nueva al cliente que elijas, con los mismos productos, precios, tasas y montos, pagada con ese crédito.`
                : `${doc.numero_formateado} se revierte con un Cambio por el total y sale una nota nueva al cliente que elijas, con los mismos productos y montos, pagada con ese crédito.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Campo etiqueta={`¿A nombre de quién sale ${esFac ? 'la factura nueva' : 'la nota nueva'}?`} htmlFor="otro-nombre-buscar">
              {cliente ? (
                <div className="flex items-center justify-between gap-2 rounded-lg border p-2" data-testid="otro-nombre-cliente">
                  <div>
                    <p className="font-medium">{cliente.razon_social}</p>
                    <p className="text-xs text-muted-foreground">{cliente.rif} · {cliente.direccion_fiscal || 'sin dirección fiscal'}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setCliente(null)}>Cambiar</Button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input id="otro-nombre-buscar" className="pl-8" placeholder="Nombre, RIF o cédula…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="otro-nombre-buscar" autoFocus />
                    </div>
                    <Button variant="outline" onClick={() => setNuevo(true)} data-testid="otro-nombre-nuevo-cliente"><UserPlus /> Cliente nuevo</Button>
                  </div>
                  {buscar ? (
                    <div className="mt-1 max-h-52 space-y-1 overflow-y-auto" data-testid="otro-nombre-resultados">
                      {lista.isLoading ? <p className="text-xs text-muted-foreground">Buscando…</p> : null}
                      {!lista.isLoading && candidatos.length === 0 ? <p className="text-xs text-muted-foreground">Ningún cliente con ese nombre o RIF. Regístralo con «Cliente nuevo».</p> : null}
                      {candidatos.map((c) => (
                        <button key={c.id} type="button" className="flex w-full items-center justify-between rounded-md border px-2 py-1.5 text-left text-sm hover:bg-muted" onClick={() => setCliente(c)} data-testid={`otro-nombre-opcion-${c.rif}`}>
                          <span><b>{c.razon_social}</b> <span className="text-xs text-muted-foreground">{c.rif}</span></span>
                          {c.tipo_contribuyente === 'especial' ? <Badge variant="fiscal">agente de retención</Badge> : null}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </Campo>
            {sinDatos ? <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="otro-nombre-sin-datos">A {cliente!.razon_social} le falta la dirección fiscal: complétala en su ficha antes de facturarle.</p> : null}
            {cliente ? (
              <div className="space-y-1 rounded-lg bg-muted/60 p-3 text-sm" data-testid="otro-nombre-resumen">
                <p className="flex items-start justify-between gap-2"><span>{esFac ? 'Nota de crédito' : 'Cambio'} por el total, a {doc.cliente_razon_social}</span><Dinero valor={doc.total} modo="triple" doc={t} bs={doc.total_bs} /></p>
                <p className="flex items-start justify-between gap-2"><span>{esFac ? 'Factura' : 'Nota'} nueva a {cliente.razon_social}, con fecha de hoy</span><Dinero valor={doc.total} modo="triple" doc={t} bs={doc.total_bs} /></p>
                <ul className="list-disc pl-5 text-xs text-muted-foreground">
                  <li>Los mismos productos, cantidades, precios, descuentos y tasas de {nombreDoc} original.</li>
                  <li>Se paga con el crédito de la {nombreCredito}: no se cobra otra vez, no se repite la referencia del pago y no lleva IGTF.</li>
                  <li>No mueve inventario: lo entregado sigue entregado y lo pendiente se retira con la nueva.</li>
                  {retiene ? <li className="text-amber-900" data-testid="otro-nombre-retencion">{cliente.razon_social} es agente de retención: la factura nueva va como «pago completo» y, cuando traiga el comprobante, se le reintegra lo retenido desde Retenciones.</li> : null}
                </ul>
              </div>
            ) : null}
            <Campo etiqueta="Motivo" htmlFor="otro-nombre-motivo">
              <Textarea id="otro-nombre-motivo" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. el cliente pidió la factura a nombre de su empresa" data-testid="otro-nombre-motivo" />
            </Campo>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cerrar}>Volver</Button>
            <Button onClick={() => void confirmar()} cargando={enviando} disabled={!cliente || sinDatos || motivo.trim().length < 3} data-testid="otro-nombre-confirmar">
              {esFac ? 'Emitir nota de crédito y factura nueva' : 'Emitir cambio y nota nueva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DialogClienteRapido abierto={nuevo} cerrar={() => setNuevo(false)} alCrear={(c) => { setCliente(c); setNuevo(false); }} />
      {aut.dialogo}
    </>
  );
}
